import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    defaultCountryAbbreviation: state.context.website.properties.defaultCountryAbbreviation,
});
const enum fields {
    canadaUrl = "canadaUrl",
    usaUrl = "usaUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.canadaUrl]: string;
        [fields.usaUrl]: string;
    };
}

export interface CountryStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    dropdown?: InjectableCss;
    iconstyled?: InjectableCss;
    fontWeightBold?: InjectableCss;
    fontWeightNormal?: InjectableCss;
}

export const countryStyles: CountryStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 103px;
            padding: 10px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            padding: 0;
                        `,
                        css`
                            margin-left: auto;
                            padding: 0;
                        `,
                    ],
                    "max",
                )}
            @media print {
                display: none;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            font-size: ${TYPOGRAPHY.size.smallest};
            text-transform: Capitalize;
            text-align: center;
            padding-left: 10px;
            width: 103px;
            font-size: 12px;
            & > div:nth-child(2) {
                padding-left: 5px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    dropdown: {
        css: css`
            background: #ffffff;
            z-index: 1290;
            box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%),
                0px 1px 10px 0px rgb(0 0 0 / 12%);
            list-style: none;
            position: absolute;
            top: 36px;
            margin: 0;
            padding: 0;
            border-top: 2px solid #0b70b7;
            border-radius: 0px 0px 8px 8px;

            > a,
            > span {
                padding: 6px 10px;
                text-decoration: none;
                width: 103px;
                font-size: ${TYPOGRAPHY.size.smaller};
                &:first-child {
                    border-bottom: 1px solid #d6e4f4;
                }
                > span {
                    color: ${COLORS.black} !important;
                    text-transform: Capitalize;
                    font-size: 13px !important;
                    > div {
                        vertical-align: top;
                        margin-right: 5px;
                    }
                }
                &:focus {
                    outline: none;
                }
            }
            @media (max-width: 1199px) and (min-width: 992px) {
                top: 34px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            top: 23px;
                            > a,
                            > span {
                                width: 100%;
                                padding: 5px 5px 5px 10px;
                            }
                            &:focus {
                                outline: none;
                            }
                        `,
                        css`
                            top: 23px;
                            > a,
                            > span {
                                padding: 5px 10px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    iconstyled: {
        css: css`
            width: 18px;
            height: auto;
            svg {
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 18px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    fontWeightBold: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
};

const styles = countryStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const Country = ({ defaultCountryAbbreviation, fields }: Props) => {
    const [dropdown, dropdownIsOpen] = useState(false);
    const closeHandler = () => {
        dropdownIsOpen(false);
    };
    const DropDownMenu: any = useRef();
    useEffect(() => {
        document.addEventListener("mousedown", event => {
            if (!DropDownMenu.current.contains(event.target)) {
                dropdownIsOpen(false);
            }
        });
    });
    return (
        <StyledWrapper {...styles.wrapper} ref={DropDownMenu}>
            <StyledWrapper {...styles.container} onClick={() => dropdownIsOpen(!dropdown)}>
                {defaultCountryAbbreviation === "CA" ? (
                    <>
                        <LazyImage src="/userfiles/images/icons/Canada.svg" />
                        <StyledWrapper>{translate("Canada")}</StyledWrapper>
                        <Icon src={ChevronDown} {...styles.iconstyled} />
                    </>
                ) : (
                    <>
                        <LazyImage src="/userfiles/images/icons/united-states.svg" />
                        <StyledWrapper>{translate("USA")}</StyledWrapper>
                        <Icon src={ChevronDown} {...styles.iconstyled} />
                    </>
                )}
            </StyledWrapper>
            {dropdown && (
                <StyledWrapper {...styles.dropdown} onClick={() => dropdownIsOpen(!dropdown)}>
                    <Link
                        href={fields.canadaUrl}
                        target="_blank"
                        onClick={closeHandler}
                        {...(defaultCountryAbbreviation === "CA" ? { ...styles.fontWeightBold } : null)}
                    >
                        <LazyImage src="/userfiles/images/icons/Canada.svg" />
                        Canada
                    </Link>
                    <Link
                        href={fields.usaUrl}
                        target="_blank"
                        onClick={closeHandler}
                        {...(defaultCountryAbbreviation === "US" ? { ...styles.fontWeightBold } : null)}
                    >
                        <LazyImage src="/userfiles/images/icons/united-states.svg" />
                        USA
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Country),
    definition: {
        displayName: "Country Dropdown",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.canadaUrl,
                displayName: " Please Provide link for Canada",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                placeholder: "URL",
                isRequired: true,
            },
            {
                name: fields.usaUrl,
                displayName: "Please Provide link for USA",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                placeholder: "URL",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
